"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  X,
  ExternalLink,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  Circle,
  ArrowRight,
  Shield,
  Sparkles,
} from "lucide-react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useSpring,
  type MotionValue,
} from "motion/react";
import { cn } from "@/lib/utils";
import { BrandIcon } from "@/components/brand-icons/brand-icon";
import type { ConnectionTemplate } from "./templates-data";
import type { ConnectionConfig } from "../connection-studio";

interface TemplateFormSlideProps {
  template: ConnectionTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (config: Partial<ConnectionConfig>) => void;
}

const FIELD_LABELS: Record<string, string> = {
  tenant_id: "Tenant ID",
  client_id: "Client ID",
  client_secret: "Client Secret",
  subscription_id: "Subscription ID",
  org_name: "Organization Name",
  sharepoint_tenant: "SharePoint Tenant",
  service_account_email: "Service Account Email",
  private_key: "Private Key (JSON)",
  scopes: "Scopes",
  delegated_user_email: "Delegated User Email",
  access_key_id: "Access Key ID",
  secret_access_key: "Secret Access Key",
  region: "AWS Region",
  service: "Service Name",
  session_token: "Session Token",
  api_id: "API ID",
  stage: "Stage",
  token: "Access Token",
  api_key: "API Key",
  account_sid: "Account SID",
  auth_token: "Auth Token",
  access_token: "Access Token",
  integration_token: "Integration Token",
  host: "Host",
  port: "Port",
  database: "Database Name",
  username: "Username",
  password: "Password",
  ssl_mode: "SSL Mode",
  connection_string: "Connection String",
  project_ref: "Project Reference",
  domain: "Domain",
  email: "Email",
  api_token: "API Token",
  base_url: "Base URL",
  project_id: "Project ID",
  scope: "OAuth Scope",
  instance: "Instance",
  api_version: "API Version",
};

const FIELD_PLACEHOLDERS: Record<string, string> = {
  tenant_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  client_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  client_secret: "Your client secret",
  subscription_id: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  org_name: "contoso",
  sharepoint_tenant: "contoso",
  service_account_email: "service-account@project.iam.gserviceaccount.com",
  private_key: '{"type": "service_account", ...}',
  scopes: "https://www.googleapis.com/auth/gmail.readonly",
  delegated_user_email: "user@domain.com",
  access_key_id: "AKIAIOSFODNN7EXAMPLE",
  secret_access_key: "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY",
  region: "us-east-1",
  service: "s3",
  session_token: "Optional session token",
  api_id: "abc123def",
  stage: "prod",
  token: "ghp_xxxxxxxxxxxx",
  api_key: "sk_live_xxxxxxxxxxxx",
  account_sid: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
  auth_token: "Your auth token",
  access_token: "Bearer token",
  integration_token: "secret_xxxxxxxxxxxx",
  host: "localhost",
  port: "5432",
  database: "mydb",
  username: "postgres",
  password: "Your password",
  ssl_mode: "prefer",
  connection_string: "postgresql://user:pass@host:5432/db",
  project_ref: "abcdefghijklmnop",
  domain: "yourcompany",
  email: "you@company.com",
  api_token: "Your API token",
  base_url: "https://api.example.com",
  project_id: "my-project-123",
  scope: "https://graph.microsoft.com/.default",
  instance: "your-instance",
  api_version: "v1",
};

const SENSITIVE_FIELDS = [
  "client_secret",
  "secret_access_key",
  "session_token",
  "password",
  "api_key",
  "auth_token",
  "access_token",
  "token",
  "integration_token",
  "api_token",
  "private_key",
];

const AUTH_TYPE_LABELS: Record<string, string> = {
  oauth2_client: "OAuth 2.0",
  jwt: "JWT / Service Account",
  aws_signature: "AWS Signature V4",
  bearer_token: "Bearer Token",
  api_key: "API Key",
  basic_auth: "Basic Auth",
  none: "No Authentication",
};

/* ── Category accent colour mapping ── */
const CATEGORY_COLORS: Record<string, { glow: string; ring: string; text: string }> = {
  microsoft: { glow: "rgba(59,130,246,0.12)", ring: "border-blue-500/25", text: "text-blue-400" },
  google: { glow: "rgba(56,189,248,0.12)", ring: "border-sky-500/25", text: "text-sky-400" },
  aws: { glow: "rgba(245,158,11,0.12)", ring: "border-amber-500/25", text: "text-amber-400" },
  api: { glow: "rgba(20,184,166,0.12)", ring: "border-teal-500/25", text: "text-teal-400" },
  database: { glow: "rgba(139,92,246,0.12)", ring: "border-violet-500/25", text: "text-violet-400" },
  document: { glow: "rgba(244,63,94,0.12)", ring: "border-rose-500/25", text: "text-rose-400" },
  generic: { glow: "rgba(148,163,184,0.08)", ring: "border-slate-500/25", text: "text-slate-400" },
};

/* ── Animated input field ── */
function AnimatedField({
  field,
  index,
  isRequired,
  isSensitive,
  isMultiline,
  value,
  showPassword,
  onChange,
  onTogglePassword,
}: {
  field: string;
  index: number;
  isRequired: boolean;
  isSensitive: boolean;
  isMultiline: boolean;
  value: string;
  showPassword: boolean;
  onChange: (val: string) => void;
  onTogglePassword: () => void;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const isFilled = value.trim().length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 + index * 0.04, type: "spring", stiffness: 400, damping: 30 }}
      className="group/field"
    >
      <div className="mb-1.5 flex items-center gap-2">
        <motion.div
          animate={{
            scale: isFilled ? 1 : 0.85,
            opacity: isFilled ? 1 : 0.3,
          }}
          transition={{ type: "spring", stiffness: 500, damping: 25 }}
        >
          {isFilled ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
          ) : (
            <Circle className="h-3 w-3 text-white/20" />
          )}
        </motion.div>
        <label className="text-[13px] font-medium tracking-wide text-white/55">
          {FIELD_LABELS[field] || field}
          {isRequired && (
            <span className="ml-0.5 text-teal-400/70">*</span>
          )}
        </label>
        {!isRequired && (
          <span className="rounded-full bg-white/[0.04] px-1.5 py-0 text-[9px] text-white/25">
            optional
          </span>
        )}
      </div>
      <motion.div
        className="relative"
        animate={{
          scale: isFocused ? 1.005 : 1,
        }}
        transition={{ type: "spring", stiffness: 600, damping: 35 }}
      >
        {/* Focus glow ring */}
        <motion.div
          className="pointer-events-none absolute -inset-px rounded-[10px]"
          animate={{
            opacity: isFocused ? 1 : 0,
            boxShadow: isFocused
              ? "0 0 0 1px rgba(20,184,166,0.3), 0 0 16px rgba(20,184,166,0.08)"
              : "0 0 0 1px transparent, 0 0 0 transparent",
          }}
          transition={{ duration: 0.2 }}
        />
        {isMultiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={FIELD_PLACEHOLDERS[field] || `Enter ${field}`}
            className="min-h-[110px] w-full rounded-[10px] border border-white/[0.07] bg-white/[0.025] px-3.5 py-3 font-mono text-[12px] leading-relaxed text-white/90 placeholder:text-white/20 outline-none transition-colors focus:border-teal-500/40 focus:bg-white/[0.04]"
          />
        ) : (
          <input
            type={isSensitive && !showPassword ? "password" : "text"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={FIELD_PLACEHOLDERS[field] || `Enter ${field}`}
            className={cn(
              "h-10 w-full rounded-[10px] border border-white/[0.07] bg-white/[0.025] px-3.5 font-mono text-[12px] text-white/90 placeholder:text-white/20 outline-none transition-colors focus:border-teal-500/40 focus:bg-white/[0.04]",
              isSensitive && "pr-10",
            )}
          />
        )}
        {isSensitive && !isMultiline && (
          <button
            type="button"
            className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-white/30 transition-all hover:bg-white/[0.06] hover:text-white/50"
            onClick={onTogglePassword}
          >
            {showPassword ? (
              <EyeOff className="h-3.5 w-3.5" />
            ) : (
              <Eye className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Progress arc ── */
function ProgressArc({ progress }: { progress: number }) {
  const circumference = 2 * Math.PI * 18;
  const springProgress = useSpring(0, { stiffness: 120, damping: 20 });

  useEffect(() => {
    springProgress.set(progress);
  }, [progress, springProgress]);

  const dashOffset = useTransform(
    springProgress,
    (v: number) => circumference - (v / 100) * circumference,
  );

  return (
    <svg width="44" height="44" viewBox="0 0 44 44" className="rotate-[-90deg]">
      <circle
        cx="22"
        cy="22"
        r="18"
        fill="none"
        stroke="rgba(255,255,255,0.04)"
        strokeWidth="3"
      />
      <motion.circle
        cx="22"
        cy="22"
        r="18"
        fill="none"
        stroke="url(#progressGrad)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        style={{ strokeDashoffset: dashOffset }}
      />
      <defs>
        <linearGradient id="progressGrad" x1="0" y1="0" x2="44" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#2dd4bf" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ── Ambient glow following mouse ── */
function AmbientGlow({
  glowX,
  glowY,
  color,
}: {
  glowX: MotionValue<number>;
  glowY: MotionValue<number>;
  color: string;
}) {
  const bg = useTransform(
    glowX,
    (x) => `radial-gradient(400px circle at ${x}px ${glowY.get()}px, ${color}, transparent 70%)`,
  );

  return (
    <motion.div
      className="pointer-events-none absolute -inset-40 z-0 opacity-50"
      style={{ background: bg }}
    />
  );
}

/* ── Main Component ── */
export function TemplateFormSlide({
  template,
  isOpen,
  onClose,
  onSubmit,
}: TemplateFormSlideProps) {
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [connectionName, setConnectionName] = useState("");
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const glowX = useSpring(mouseX, { stiffness: 150, damping: 20 });
  const glowY = useSpring(mouseY, { stiffness: 150, damping: 20 });

  useEffect(() => {
    if (template) {
      const initialValues: Record<string, string> = {};
      const configDefaults = template.config_template || {};

      [
        ...template.required_fields,
        ...(template.optional_fields || []),
      ].forEach((field) => {
        if (configDefaults[field] !== undefined) {
          initialValues[field] = String(configDefaults[field]);
        } else {
          initialValues[field] = "";
        }
      });

      setFormValues(initialValues);
      setConnectionName(`My ${template.name} Connection`);
      setShowSensitive({});
    }
  }, [template]);

  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      setTimeout(() => nameInputRef.current?.select(), 350);
    }
  }, [isOpen]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left);
    mouseY.set(e.clientY - rect.top);
  };

  const handleSubmit = async () => {
    if (!template) return;
    setIsSubmitting(true);

    try {
      let baseUrl = template.base_url;
      const authConfig: Record<string, unknown> = { ...template.auth_config_template };
      const config: Record<string, unknown> = { ...template.config_template };

      Object.entries(formValues).forEach(([key, value]) => {
        baseUrl = baseUrl.replace(`{${key}}`, value);
        Object.keys(authConfig).forEach((authKey) => {
          if (typeof authConfig[authKey] === "string") {
            authConfig[authKey] = (authConfig[authKey] as string).replace(`{${key}}`, value);
          }
        });
      });

      if (template.auth_type === "oauth2_client") {
        authConfig.client_id = formValues.client_id;
        authConfig.client_secret = formValues.client_secret;
        if (formValues.scope) authConfig.scope = formValues.scope;
      } else if (template.auth_type === "jwt") {
        authConfig.service_account_email = formValues.service_account_email;
        authConfig.private_key = formValues.private_key;
        if (formValues.delegated_user_email) authConfig.delegated_user_email = formValues.delegated_user_email;
        if (formValues.scopes) config.scopes = formValues.scopes.split(",").map((s: string) => s.trim());
      } else if (template.auth_type === "aws_signature") {
        authConfig.access_key_id = formValues.access_key_id;
        authConfig.secret_access_key = formValues.secret_access_key;
        if (formValues.session_token) authConfig.session_token = formValues.session_token;
        config.region = formValues.region;
        config.service = formValues.service || template.config_template?.service;
      } else if (template.auth_type === "bearer_token") {
        authConfig.token = formValues.token || formValues.api_key || formValues.access_token || formValues.integration_token;
      } else if (template.auth_type === "api_key") {
        authConfig.api_key = formValues.api_key || formValues.password;
        authConfig.api_key_name = template.auth_config_template?.api_key_name || "Authorization";
        authConfig.api_key_location = template.auth_config_template?.api_key_location || "header";
      } else if (template.auth_type === "basic_auth") {
        authConfig.username = formValues.username || formValues.account_sid || formValues.email;
        authConfig.password = formValues.password || formValues.auth_token || formValues.api_token;
      }

      if (template.type === "database") {
        if (formValues.connection_string) {
          config.connection_string = formValues.connection_string;
        } else {
          config.host = formValues.host;
          config.port = parseInt(formValues.port) || template.config_template?.port;
          config.database = formValues.database;
        }
        if (formValues.ssl_mode) config.ssl_mode = formValues.ssl_mode;
      }

      if (template.openapi_url) config.openapi_url = template.openapi_url;

      onSubmit({
        name: connectionName,
        description: `${template.name} connection created via Quick Connect`,
        type: template.type,
        base_url: baseUrl,
        auth_type: template.auth_type,
        auth_config: authConfig,
        config,
        headers: (template.config_template?.headers as Record<string, string>) || {},
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (!connectionName.trim() || !template) return false;
    return template.required_fields.every((field) => {
      const value = formValues[field];
      return value && value.trim().length > 0;
    });
  };

  const toggleSensitive = (field: string) => {
    setShowSensitive((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const allFields = useMemo(
    () => (template ? [...template.required_fields, ...(template.optional_fields || [])] : []),
    [template],
  );

  const filledRequiredCount = useMemo(() => {
    if (!template) return 0;
    return template.required_fields.filter((f) => formValues[f]?.trim()).length;
  }, [template, formValues]);

  const totalRequired = template?.required_fields.length || 1;
  const progressPercent = Math.round(
    ((filledRequiredCount + (connectionName.trim() ? 1 : 0)) / (totalRequired + 1)) * 100,
  );

  const colors = template ? (CATEGORY_COLORS[template.category] || CATEGORY_COLORS.generic) : CATEGORY_COLORS.generic;
  const valid = isFormValid();

  return createPortal(
    <AnimatePresence>
      {isOpen && template && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Dialog container */}
          <div className="fixed inset-0 z-[81] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: "spring", stiffness: 350, damping: 30 }}
              className="relative flex w-full max-w-[580px] max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0b0f1a] shadow-[0_32px_80px_rgba(0,0,0,0.5)]"
              onMouseMove={handleMouseMove}
            >
              {/* Ambient glow that follows mouse */}
              <AmbientGlow glowX={glowX} glowY={glowY} color={colors.glow} />

              {/* Top accent line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-x-0 top-0 h-px origin-left bg-gradient-to-r from-transparent via-teal-500/40 to-transparent"
              />

              {/* Close button — pinned to dialog chrome, above hero */}
              <motion.button
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 400, damping: 25 }}
                onClick={onClose}
                className="absolute right-3 top-3 z-20 flex h-7 w-7 items-center justify-center rounded-full text-white/25 transition-all hover:bg-white/[0.08] hover:text-white/50"
              >
                <X className="h-3.5 w-3.5" />
              </motion.button>

              {/* ── Hero header ── */}
              <div className="relative z-10 px-7 pt-7 pb-0">
                {/* Brand icon + info row */}
                <div className="flex items-start gap-5">
                  {/* Brand icon with glow */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 22 }}
                    className="relative flex-shrink-0"
                  >
                    <div
                      className="absolute -inset-3 rounded-2xl blur-xl opacity-60"
                      style={{ background: colors.glow }}
                    />
                    <div className={cn(
                      "relative flex h-16 w-16 items-center justify-center rounded-2xl border bg-[#0e1325]/80 p-2.5 backdrop-blur-sm",
                      colors.ring,
                    )}>
                      <BrandIcon templateId={template.id} size={40} />
                    </div>
                  </motion.div>

                  {/* Title + meta */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.18, type: "spring", stiffness: 400, damping: 30 }}
                    className="min-w-0 flex-1 pt-1"
                  >
                    <h2 className="text-[18px] font-bold tracking-tight text-white/95">
                      {template.name}
                    </h2>
                    <p className="mt-0.5 text-[12px] leading-relaxed text-white/40">
                      {template.description}
                    </p>
                    <div className="mt-2.5 flex items-center gap-2">
                      <span className="flex items-center gap-1 rounded-md bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/40">
                        <Shield className="h-2.5 w-2.5" />
                        {AUTH_TYPE_LABELS[template.auth_type] || template.auth_type}
                      </span>
                      {template.openapi_url && (
                        <span className="flex items-center gap-1 rounded-md bg-teal-500/8 px-2 py-0.5 text-[10px] text-teal-400/60">
                          <Sparkles className="h-2.5 w-2.5" />
                          OpenAPI
                        </span>
                      )}
                    </div>
                  </motion.div>

                  {/* Progress ring */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 350, damping: 25 }}
                    className="relative flex-shrink-0"
                  >
                    <ProgressArc progress={progressPercent} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[13px] font-semibold tabular-nums text-white/60">
                        {progressPercent}%
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Setup guide */}
                {template.setup_guide_url && (
                  <motion.a
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, type: "spring", stiffness: 400, damping: 30 }}
                    href={template.setup_guide_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 flex items-center gap-2 rounded-lg border border-white/[0.05] bg-white/[0.02] px-3.5 py-2.5 text-[13px] font-medium text-white/40 transition-all hover:border-teal-500/20 hover:bg-teal-500/5 hover:text-teal-300"
                  >
                    <ExternalLink className="h-3 w-3" />
                    View setup guide for {template.name}
                    <ArrowRight className="ml-auto h-3 w-3 opacity-40" />
                  </motion.a>
                )}
              </div>

              {/* Divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-7 mt-5 h-px origin-left bg-gradient-to-r from-white/[0.06] via-white/[0.04] to-transparent"
              />

              {/* ── Form fields (scrollable) ── */}
              <div className="relative z-10 flex-1 space-y-4 overflow-y-auto px-7 py-5 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10">
                {/* Connection Name */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12, type: "spring", stiffness: 400, damping: 30 }}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    <motion.div
                      animate={{
                        scale: connectionName.trim() ? 1 : 0.85,
                        opacity: connectionName.trim() ? 1 : 0.3,
                      }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    >
                      {connectionName.trim() ? (
                        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                      ) : (
                        <Circle className="h-3 w-3 text-white/20" />
                      )}
                    </motion.div>
                    <label className="text-[13px] font-medium tracking-wide text-white/55">
                      Connection Name
                      <span className="ml-0.5 text-teal-400/70">*</span>
                    </label>
                  </div>
                  <motion.div
                    className="relative"
                    animate={{ scale: nameFocused ? 1.005 : 1 }}
                    transition={{ type: "spring", stiffness: 600, damping: 35 }}
                  >
                    <motion.div
                      className="pointer-events-none absolute -inset-px rounded-[10px]"
                      animate={{
                        opacity: nameFocused ? 1 : 0,
                        boxShadow: nameFocused
                          ? "0 0 0 1px rgba(20,184,166,0.3), 0 0 16px rgba(20,184,166,0.08)"
                          : "0 0 0 1px transparent, 0 0 0 transparent",
                      }}
                      transition={{ duration: 0.2 }}
                    />
                    <input
                      ref={nameInputRef}
                      value={connectionName}
                      onChange={(e) => setConnectionName(e.target.value)}
                      onFocus={() => setNameFocused(true)}
                      onBlur={() => setNameFocused(false)}
                      placeholder="My Connection"
                      className="h-10 w-full rounded-[10px] border border-white/[0.07] bg-white/[0.025] px-3.5 text-[13px] text-white/90 placeholder:text-white/20 outline-none transition-colors focus:border-teal-500/40 focus:bg-white/[0.04]"
                    />
                  </motion.div>
                </motion.div>

                {/* Dynamic fields */}
                {allFields.map((field, idx) => {
                  const isRequired = template.required_fields.includes(field);
                  const isSensitive = SENSITIVE_FIELDS.includes(field);
                  const isMultiline = field === "private_key";

                  return (
                    <AnimatedField
                      key={field}
                      field={field}
                      index={idx}
                      isRequired={isRequired}
                      isSensitive={isSensitive}
                      isMultiline={isMultiline}
                      value={formValues[field] || ""}
                      showPassword={!!showSensitive[field]}
                      onChange={(val) =>
                        setFormValues((prev) => ({ ...prev, [field]: val }))
                      }
                      onTogglePassword={() => toggleSensitive(field)}
                    />
                  );
                })}
              </div>

              {/* ── Footer ── */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, type: "spring", stiffness: 400, damping: 30 }}
                className="relative z-10 flex items-center gap-3 border-t border-white/[0.06] px-7 py-4"
              >
                {/* Field count status */}
                <div className="flex-1 text-[13px] text-white/30">
                  <span className="tabular-nums text-white/50">{filledRequiredCount}</span>
                  <span> / {totalRequired} required</span>
                </div>

                <button
                  onClick={onClose}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-5 py-2.5 text-[12px] font-medium text-white/40 transition-all hover:bg-white/[0.06] hover:text-white/60"
                >
                  Cancel
                </button>

                <motion.button
                  onClick={handleSubmit}
                  disabled={!valid || isSubmitting}
                  whileHover={valid ? { scale: 1.02 } : undefined}
                  whileTap={valid ? { scale: 0.98 } : undefined}
                  className={cn(
                    "relative overflow-hidden rounded-lg px-6 py-2.5 text-[12px] font-semibold transition-all",
                    valid
                      ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg shadow-teal-500/20 hover:shadow-teal-500/30"
                      : "bg-white/[0.04] text-white/20 cursor-not-allowed",
                  )}
                >
                  {/* Button shimmer */}
                  {valid && !isSubmitting && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                      initial={{ x: "-100%" }}
                      animate={{ x: "200%" }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    />
                  )}
                  <span className="relative flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-3.5 w-3.5" />
                      </>
                    )}
                  </span>
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
